import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import SummarizeIcon from '@mui/icons-material/Summarize';
import api from '../../shared/services/api';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

type ValidationStatusType = 'complete' | 'incomplete' | 'error';

interface SummaryTabProps {
  declaration: any;
  formData: any;
  validationStatus: { [key: string]: ValidationStatusType };
  onValidate: (status: { [key: string]: ValidationStatusType }) => void;
}

const SummaryTab: React.FC<SummaryTabProps> = ({
  declaration,
  formData,
  validationStatus,
  onValidate
}) => {
  const { t, tString } = useTranslation();
  const [validationResults, setValidationResults] = useState<any>(null);
  const [validating, setValidating] = useState(false);
  const [exportFormat, setExportFormat] = useState<'xml' | 'json' | 'pdf'>('xml');

  useEffect(() => {
    validateDeclaration();
  }, []);

  const validateDeclaration = async () => {
    try {
      setValidating(true);
      const response = await api.post(
        `/api/v1/customs/declarations/${declaration.id}/validate`
      );
      setValidationResults(response.data);

      // Update tab validation status
      const status: { [key: string]: ValidationStatusType } = {
        general: 'complete',
        parties: 'complete',
        items: 'complete',
        documents: 'complete',
        transport: 'complete',
        summary: 'complete'
      };

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((error: any) => {
          const section = error.field?.split('.')[0] || 'general';
          status[section] = 'error' as ValidationStatusType;
        });
      }

      if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach((warning: any) => {
          const section = warning.field?.split('.')[0] || 'general';
          if (status[section] !== 'error') {
            status[section] = 'incomplete' as ValidationStatusType;
          }
        });
      }

      onValidate(status);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleExport = async (format: 'xml' | 'json' | 'pdf') => {
    try {
      const response = await api.get(
        `/api/v1/customs/declarations/${declaration.id}/export`,
        {
          params: { format },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `declaration_${declaration.reference_number}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const calculateTotals = () => {
    const items = formData.items || [];
    return {
      totalValue: items.reduce((sum: number, item: any) => sum + (item.total_value || 0), 0),
      totalWeight: items.reduce((sum: number, item: any) => sum + (item.gross_weight || 0), 0),
      itemCount: items.length
    };
  };

  const totals = calculateTotals();
  const isValid = validationResults?.valid || false;
  const hasErrors = validationResults?.errors && validationResults.errors.length > 0;
  const hasWarnings = validationResults?.warnings && validationResults.warnings.length > 0;

  return (
    <Box>
      <SectionHeader
        title={t('customsDeclaration.summary.title') as string}
        subtitle={t('customsDeclaration.summary.subtitle') as string}
        icon={<SummarizeIcon color="primary" sx={{ fontSize: 32 }} />}
      />

      {/* Validation Status */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" gutterBottom>
              Validation Status
            </Typography>
            {validating ? (
              <Typography variant="body2" color="text.secondary">
                Validating declaration...
              </Typography>
            ) : isValid ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                Declaration is valid and ready for submission
              </Alert>
            ) : hasErrors ? (
              <Alert severity="error" icon={<ErrorIcon />}>
                Declaration has validation errors that must be fixed
              </Alert>
            ) : hasWarnings ? (
              <Alert severity="warning" icon={<WarningIcon />}>
                Declaration has warnings but can be submitted
              </Alert>
            ) : null}
          </Box>
          <Button variant="outlined" onClick={validateDeclaration} disabled={validating}>
            Re-validate
          </Button>
        </Stack>

        {/* Validation Errors */}
        {hasErrors && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Errors ({validationResults.errors.length})
            </Typography>
            <List dense>
              {validationResults.errors.map((error: any, index: number) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={error.message}
                    secondary={error.field}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Validation Warnings */}
        {hasWarnings && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="warning" gutterBottom>
              Warnings ({validationResults.warnings.length})
            </Typography>
            <List dense>
              {validationResults.warnings.map((warning: any, index: number) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={warning.message}
                    secondary={warning.field}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* Declaration Summary */}
      <Grid container spacing={3}>
        {/* General Info */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              General Information
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Reference Number</TableCell>
                  <TableCell><strong>{declaration?.reference_number}</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Declaration Type</TableCell>
                  <TableCell>{declaration?.declaration_type?.toUpperCase()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell>{declaration?.country_code}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Currency</TableCell>
                  <TableCell>{formData.general_info?.currency || 'EUR'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Chip
                      label={declaration?.status?.toUpperCase()}
                      size="small"
                      color={declaration?.status === 'submitted' ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Parties Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Parties
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Exporter</TableCell>
                  <TableCell>{formData.parties?.exporter?.name || 'Not set'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Importer</TableCell>
                  <TableCell>{formData.parties?.importer?.name || 'Not set'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Declarant</TableCell>
                  <TableCell>{formData.parties?.declarant?.name || 'Not set'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Representative</TableCell>
                  <TableCell>{formData.parties?.representative?.name || 'Optional'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Items Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Items Summary
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Total Items</TableCell>
                  <TableCell><strong>{totals.itemCount}</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Value</TableCell>
                  <TableCell>
                    <strong>
                      {totals.totalValue.toFixed(2)} {formData.general_info?.currency || 'EUR'}
                    </strong>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Weight</TableCell>
                  <TableCell><strong>{totals.totalWeight.toFixed(2)} kg</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Documents Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Documents
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Supporting Documents</TableCell>
                  <TableCell><strong>{formData.documents?.length || 0}</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Transport Mode</TableCell>
                  <TableCell>{formData.transport?.mode?.toUpperCase() || 'Not set'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Incoterms</TableCell>
                  <TableCell>{formData.transport?.incoterm || 'Not set'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* Export Options */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Export Declaration
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Download the declaration in various formats
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('xml')}
          >
            Export as XML
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('json')}
          >
            Export as JSON
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('pdf')}
          >
            Export as PDF
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default SummaryTab;
